import { RunStatus, WorkflowSubmission } from '@aero-platform/shared'
import { Button } from 'primereact/button'
import { DataTablePageEvent, DataTableSortEvent, SortOrder } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { RadioButton } from 'primereact/radiobutton'
import { FC, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import {
  useAddWorkflowRunGroup,
  useGetSubmissions,
  useRefreshMethodology
} from '../../api/submission-API'
import IconButton from '../../components/buttons/IconButton'
import CloseButton from '../../components/buttons/CloseButton'
import { RefreshIcon } from '../../components/Icons'
import PostProcImagesModal from '../../components/submission/PostProcImagesModal'
import { protoService } from '../../proto/ProtoService'
import { LazyTableState } from '../../types/types'
import { computeSupersededIds, getSupersedeCaseKey } from '../../types/submission-util'
import { currentUserState } from '../../utils/state-atoms'
import { canMutate } from '../../utils/roles'
import CaseView from './CaseView'
import RunView from './RunView'
import SubmissionFilters, { SUBMISSION_VIEW_MODE_KEY } from './SubmissionFilters'

const pageSizeOptions = [100, 200, 500]

const projection = encodeURIComponent(
  JSON.stringify({
    _id: 1,
    name: 1,
    shortName: 1,
    description: 1,
    overallStatus: 1,
    timeCreated: 1,
    timeUpdated: 1,
    createByUserName: 1,
    projectId: 1,
    projectName: 1,
    submitUser: 1,
    workflowSubmissionId: 1,
    submissionRunGroups: {
      name: 1,
      workflowRunGroups: {
        name: 1,
        overallStatus: 1,
        systemRunId: 1,
        workflow: {
          _id: 1,
          name: 1,
          systemRunId: 1,
          overallStatus: 1,
          workflowTasks: {
            _id: 1,
            raceTeamId: 1,
            name: 1,
            status: {
              runStatus: 1,
              httpResultText: 1
            },
            systemRunId: 1
          }
        }
      }
    },
    runConfig: {
      description: 1,
      referenceRunName: 1,
      baselineRunName: 1
    }
  })
)

const SubmissionListPage: FC = () => {
  const [viewMode, setViewMode] = useState<'run' | 'case'>(() =>
    localStorage.getItem(SUBMISSION_VIEW_MODE_KEY) === 'case' ? 'case' : 'run'
  )
  const [formattedFilterQuery, setFormattedFilterQuery] = useState('')

  const [{ loading: submittingTask }, addWorkflowRunGroup] = useAddWorkflowRunGroup()
  const [, refreshMethodology] = useRefreshMethodology()
  const [{ data: submissionData, loading: loadingSubmissions }, fetchSubmissions] =
    useGetSubmissions()

  const currentUser = useRecoilValue(currentUserState)
  const [submissions, setSubmissions] = useState<WorkflowSubmission[]>([])
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [selectedSubmissions, setSelectedSubmissions] = useState<WorkflowSubmission[]>([])

  const [lazyState, setLazyState] = useState<LazyTableState>({
    first: 0,
    page: 0,
    rows: pageSizeOptions[0],
    filters: {}
  } as LazyTableState)
  const [sortField, setSortField] = useState<string>('timeCreated')
  const [sortOrder, setSortOrder] = useState<SortOrder>(-1)

  const [imagesModalCase, setImagesModalCase] = useState<{
    systemRunId: string
    runId: string
    caseName: string
    baselineRunId?: string
    ref2RunId?: string
  } | null>(null)

  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false)
  const [resubmitScope, setResubmitScope] = useState<'errored' | 'all'>('errored')

  useEffect(() => {
    let query = formattedFilterQuery ? `${formattedFilterQuery}&` : ''
    query += `proj=${projection}&skip=${lazyState.first}&limit=${lazyState.rows}&countTotal=true`

    if (sortField) {
      const sort = {
        [sortField]: sortOrder
      }
      query = `${query}&sort=${JSON.stringify(sort)}`
    }

    fetchSubmissions(query).catch(err => {
      console.error(err)
      toast.error('Error loading submissions.')
    })
  }, [
    fetchSubmissions,
    formattedFilterQuery,
    lazyState.first,
    lazyState.rows,
    sortField,
    sortOrder
  ])

  useEffect(() => {
    if (!submissionData) return

    const subList = protoService.decodeProtoResponse(submissionData, 'workflowsubmission')
    if (submissionData.resultString) {
      const result = JSON.parse(submissionData.resultString)
      setTotalRecords(result.totalCount)
    }

    const processedSubList = subList.map((submission: WorkflowSubmission) => {
      let submittedByName = ''
      if (submission.submitUser) {
        if (submission.submitUser.firstName || submission.submitUser.lastName) {
          submittedByName = `${submission.submitUser.firstName} ${submission.submitUser.lastName}`
        } else {
          submittedByName = submission.submitUser.email || ''
        }
      } else if (submission.overallStatus === RunStatus.CREATED) {
        submittedByName = submission.createByUserName || 'Unknown User'
      }

      return {
        ...submission,
        submittedByName
      }
    })

    setSubmissions(processedSubList)
  }, [submissionData])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'a') {
        event.preventDefault()
        setSelectedSubmissions(submissions.filter(sub => sub.overallStatus === RunStatus.ERROR))
      }
      if (event.ctrlKey && event.key === 'd') {
        event.preventDefault()
        setSelectedSubmissions([])
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [submissions])

  const handleDataPage = (event: DataTablePageEvent) => {
    setLazyState(prev => ({
      ...prev,
      first: event.first,
      page: event.page as number,
      rows: event.rows
    }))
  }

  const handleSort = (evt: DataTableSortEvent) => {
    setSortField(evt.sortField)
    setSortOrder(evt.sortOrder)
    setLazyState(prev => ({
      ...prev,
      first: 0,
      page: 0
    }))
  }

  const handleFilterChange = useCallback((query: string) => {
    setFormattedFilterQuery(prev => (prev === query ? prev : query))
    setLazyState(prev => {
      if (prev.first === 0 && prev.page === 0) {
        return prev
      }
      return { ...prev, first: 0, page: 0 }
    })
    setSelectedSubmissions(prev => (prev.length === 0 ? prev : []))
  }, [])

  const handleSwitchViewMode = useCallback((mode: string) => {
    setViewMode(mode === 'case' ? 'case' : 'run')
  }, [])

  const openResubmitConfirm = () => {
    if (submittingTask || selectedSubmissions.length === 0) {
      return
    }
    setResubmitScope('errored')
    setShowResubmitConfirm(true)
  }

  const handleResubmitCases = (scope: 'errored' | 'all') => {
    if (submittingTask) {
      return
    }

    // Mirror the single "Resubmit Case" logic: resubmit by run group via addWorkflowRunGroup
    // (which appends a fresh run group), not a full workflowSubmit. scope 'all' resubmits every
    // active case; 'errored' resubmits only active errored cases. Superseding is computed across
    // all containers so prior runs are skipped, and each distinct case — including each multi-car
    // drafting following-car position — is resubmitted individually via its own sourceSystemRunId
    // so only that position re-runs instead of re-expanding every following car.
    const submissionsToResubmit: Array<{
      runId: string
      caseNames: string[]
      sourceSystemRunIds: string[]
    }> = []
    selectedSubmissions.forEach(workflowSubmission => {
      const supersededIds = computeSupersededIds(
        workflowSubmission.submissionRunGroups.flatMap(srg => srg.workflowRunGroups)
      )
      const seenKeys = new Set<string>()
      const caseNames: string[] = []
      const sourceSystemRunIds: string[] = []
      workflowSubmission.submissionRunGroups.forEach(srg =>
        srg.workflowRunGroups.forEach(wrg => {
          if (wrg.name === 'All' || supersededIds.has(wrg.systemRunId)) return
          const include =
            scope === 'all' || wrg.workflow?.some(wf => wf.overallStatus === RunStatus.ERROR)
          if (!include) return
          const key = getSupersedeCaseKey(wrg)
          if (seenKeys.has(key)) return
          seenKeys.add(key)
          if (!caseNames.includes(wrg.name)) {
            caseNames.push(wrg.name)
          }
          if (wrg.systemRunId) {
            sourceSystemRunIds.push(wrg.systemRunId)
          }
        })
      )
      if (sourceSystemRunIds.length > 0) {
        submissionsToResubmit.push({
          runId: workflowSubmission._id,
          caseNames,
          sourceSystemRunIds
        })
      }
    })

    const doResubmit = async () => {
      let successCount = 0
      let failCount = 0

      for (const { runId, caseNames, sourceSystemRunIds } of submissionsToResubmit) {
        try {
          await refreshMethodology({ runId } as any)
        } catch (error) {
          console.warn(`Failed to refresh methodology for submission ${runId}:`, error)
          toast.error('Failed to refresh methodology for a submission. Continuing with others.')
        }

        try {
          await addWorkflowRunGroup({
            runId,
            caseNames,
            sourceSystemRunId: sourceSystemRunIds
          } as any)
          successCount++
        } catch (error) {
          failCount++
          console.error(`Failed to resubmit run ${runId}:`, error)
        }
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      if (failCount > 0) {
        toast.error(`Failed to resubmit ${failCount} run${failCount > 1 ? 's' : ''}.`)
      }
      if (successCount > 0) {
        toast.success(`Successfully resubmitted ${successCount} run${successCount > 1 ? 's' : ''}.`)
      }
    }

    doResubmit().catch(err => {
      console.error(err)
      toast.error('Error during resubmit.')
    })
  }

  return (
    <div className="basis-0 grow flex flex-col items-stretch mt-6 min-w-full">
      <div className="basis-0 flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2 font-bold">
          <div className="page-title">Submissions</div>
        </div>
        <div className="flex items-center justify-end space-x-2">
          {canMutate(currentUser?.role?.roleId) && (
            <IconButton
              severity="danger"
              label="Resubmit Cases"
              additionalClassName=" !w-auto !min-w-fit items-center !px-4 !py-2 text-sm"
              icon={<RefreshIcon className="flex self-center w-3 h-3 fill-white mr-1" />}
              onClick={openResubmitConfirm}
              disabled={selectedSubmissions.length === 0}
            />
          )}
        </div>
      </div>
      <SubmissionFilters
        onFilterChange={handleFilterChange}
        onSwitchViewMode={handleSwitchViewMode}
      />
      {viewMode === 'run' ? (
        <RunView
          submissions={submissions}
          totalRecords={totalRecords}
          loading={loadingSubmissions}
          selectedSubmissions={selectedSubmissions}
          onSelectionChange={setSelectedSubmissions}
          lazyState={lazyState}
          onPage={handleDataPage}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          onOpenImages={setImagesModalCase}
        />
      ) : (
        <CaseView
          submissions={submissions}
          loading={loadingSubmissions}
          onOpenImages={setImagesModalCase}
        />
      )}
      {imagesModalCase && (
        <PostProcImagesModal
          systemRunId={imagesModalCase.systemRunId}
          runId={imagesModalCase.runId}
          caseName={imagesModalCase.caseName}
          baselineRunId={imagesModalCase.baselineRunId}
          ref2RunId={imagesModalCase.ref2RunId}
          onClose={() => setImagesModalCase(null)}
        />
      )}
      <Dialog
        header="Resubmit Cases"
        visible={showResubmitConfirm}
        onHide={() => setShowResubmitConfirm(false)}
        style={{ width: '32rem' }}
        footer={
          <div className="flex justify-end gap-2">
            <CloseButton label="Cancel" onClick={() => setShowResubmitConfirm(false)} />
            <Button
              label="Resubmit"
              icon="pi pi-replay"
              loading={submittingTask}
              onClick={() => {
                setShowResubmitConfirm(false)
                handleResubmitCases(resubmitScope)
              }}
            />
          </div>
        }>
        <div className="space-y-4 text-sm">
          <p>
            Resubmit cases for <strong>{selectedSubmissions.length}</strong> selected submission
            {selectedSubmissions.length === 1 ? '' : 's'}. Which cases should be resubmitted?
          </p>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioButton
                inputId="resubmit-errored"
                name="resubmitScope"
                checked={resubmitScope === 'errored'}
                onChange={() => setResubmitScope('errored')}
              />
              Errored cases only
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <RadioButton
                inputId="resubmit-all"
                name="resubmitScope"
                checked={resubmitScope === 'all'}
                onChange={() => setResubmitScope('all')}
              />
              All cases
            </label>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default SubmissionListPage
